import { Box, Button, Stack, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useCurrentUser from 'src/hooks/use-current-user';
import {
  resetSigningPinActionState,
  setupSigningPinRequest,
} from 'src/redux/actions/signing-pin-action';
import PinForm from './pin-form';
import SignatureRender from './signature-render';

//  THIN-7681 first-time setup: a one-time 2-step wizard (view HR signature → create PIN)
//  shown the first time a user signs without a PIN, and reusable from the profile dialog.
//  onComplete fires once the PIN is created (status refresh flips hasPin true upstream).
export default function SetupSigningWizard({
  onComplete,
  onCancel,
}: {
  onComplete?: () => void;
  onCancel?: () => void;
}) {
  const dispatch = useDispatch();
  const [step, setStep] = useState(0);
  const [
    firstName,
    lastName,
    licenseNumber,
    credentials,
    //  APIT-655: the therapist's display overrides for the signature block. Null
    //  for everyone today, which renders exactly as it did before.
    signatureLineOverride,
    credentialsLineOverride,
  ] = useCurrentUser([
    'firstName',
    'lastName',
    'licenseNumber',
    'credentials',
    'signatureLineOverride',
    'credentialsLineOverride',
  ]) as Array<string | null>;
  const signingPin = useSelector((state: any) => state.signingPinReducer);
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  useEffect(() => {
    if (signingPin?.setupSuccess) {
      dispatch(resetSigningPinActionState());
      onComplete?.();
    }
  }, [signingPin?.setupSuccess, dispatch, onComplete]);

  return (
    <Box>
      <Typography variant="h6">Set up signing</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A quick one-time setup before your first signature.
      </Typography>
      <Stepper activeStep={step} sx={{ mb: 3 }}>
        <Step>
          <StepLabel>Your signature</StepLabel>
        </Step>
        <Step>
          <StepLabel>Signing PIN</StepLabel>
        </Step>
      </Stepper>

      {step === 0 ? (
        <Stack spacing={2}>
          <SignatureRender
            name={fullName}
            license={licenseNumber}
            credentials={credentials}
            signatureLineOverride={signatureLineOverride}
            credentialsLineOverride={credentialsLineOverride}
            showBadge
          />
          <Typography variant="caption" color="text.secondary">
            Your signature is rendered from your name exactly as it appears in HR and licensure
            records — it can&rsquo;t be typed or edited here, so the signed name always matches your
            license. Next, you&rsquo;ll create a 4-digit signing PIN.
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            {onCancel && (
              <Button color="inherit" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button variant="contained" onClick={() => setStep(1)}>
              Next Step
            </Button>
          </Stack>
        </Stack>
      ) : (
        <PinForm
          submitLabel="Finish setup"
          createLabel="Create PIN"
          confirmLabel="Confirm PIN"
          loading={signingPin?.setupLoading}
          errorText={signingPin?.setupError?.message || null}
          warning="Write your PIN down and keep it somewhere safe. You'll need it every time you sign a document."
          onSubmit={(pin) => dispatch(setupSigningPinRequest(pin))}
          onCancel={() => setStep(0)}
          cancelLabel="Back"
        />
      )}
    </Box>
  );
}
